// URL parameter parsing + sanitization. Never trust these for anything
// security-sensitive — they only drive display copy and the avatar URL.

const MAX_NAME = 32;
const MAX_COMPANY = 32;
const ALLOWED_IMAGE_HOSTS = new Set([
  'media.licdn.com',
  'static.licdn.com',
  'media-exp1.licdn.com',
  'media-exp2.licdn.com',
  'lh3.googleusercontent.com',
  'lh4.googleusercontent.com',
  'lh5.googleusercontent.com',
  'lh6.googleusercontent.com',
  'avatars.githubusercontent.com',
  'gravatar.com',
  'www.gravatar.com',
  'cdn.ionix.io',
  'i.imgur.com',
]);

export interface GameParams {
  name: string;
  company: string;
  imageUrl: string | null;
  proxiedImageUrl: string | null;
}

const sanitizeText = (raw: string | null, fallback: string, max: number): string => {
  if (!raw) return fallback;
  const stripped = raw.replace(/[<>"'`\\]/g, '').trim();
  if (!stripped) return fallback;
  return stripped.length > max ? stripped.slice(0, max) + '…' : stripped;
};

const validateImageUrl = (raw: string | null): string | null => {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:') return null;
    if (!ALLOWED_IMAGE_HOSTS.has(u.hostname)) {
      // still accept for dev convenience but warn
      console.warn('[params] image host not on allowlist; relying on CORS or proxy:', u.hostname);
    }
    return u.toString();
  } catch {
    return null;
  }
};

export const readParams = (): GameParams => {
  const search = new URLSearchParams(window.location.search);
  const name = sanitizeText(search.get('name'), 'Hero', MAX_NAME);
  const company = sanitizeText(search.get('company'), 'your company', MAX_COMPANY);
  const imageUrl = validateImageUrl(search.get('image'));

  // If we have an image and a worker proxy is configured, route through it.
  // Otherwise we'll attempt a direct CORS load and fall back to a default.
  const proxiedImageUrl = imageUrl
    ? `/api/img?u=${encodeURIComponent(imageUrl)}`
    : null;

  return { name, company, imageUrl, proxiedImageUrl };
};
